import { PDFDocument, PDFImage, rgb } from "pdf-lib";

export interface FilledField {
  fieldId: string;
  type:
    | "signature"
    | "initials"
    | "name"
    | "date"
    | "date-now"
    | "checkbox"
    | "text"
    | "label";
  value: string;
  fontFamily?: string;
  timestamp?: string;
}

/**
 * Sanitize an SVG string by stripping potentially dangerous elements and attributes.
 *
 * ⚠️  SECURITY NOTE: sessionInput values (especially type='signature' and type='initials')
 *     contain raw SVG strings from user-drawn signatures. These are currently SAFE because
 *     they are always rasterized to canvas (Blob → Image.src → drawImage → PNG).
 *
 *     NEVER render sessionInput SVG values via innerHTML or dangerouslySetInnerHTML.
 *     If you ever need to display SVGs in the DOM, use this sanitizer first.
 */
export function sanitizeSvg(svg: string): string {
  return (
    svg
      // Strip <script> tags and their content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      // Strip event handler attributes (onload, onclick, onerror, etc.)
      .replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, "")
      // Strip javascript: URLs
      .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, "")
      // Strip data: URLs (can be used for exfiltration)
      .replace(/href\s*=\s*["']data:[^"']*["']/gi, "")
  );
}

/**
 * Renders a field value (text or SVG) to a PNG image suitable for PDF embedding.
 */
export async function renderFieldToImage(
  pdfDoc: PDFDocument,
  filled: FilledField,
  pxW: number,
  pxH: number,
  options: { contain?: boolean } = {},
): Promise<PDFImage> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  // Use higher resolution for crispness
  const scale = 4;
  canvas.width = pxW * scale;
  canvas.height = pxH * scale;

  const val = (filled.value || "").trim();
  const isSvg = val.startsWith("<svg") || val.startsWith("<?xml");

  if (isSvg) {
    let svgContent = sanitizeSvg(val); // Defense-in-depth: strip scripts/handlers even though canvas path is safe
    if (!svgContent.includes("xmlns=")) {
      svgContent = svgContent.replace(
        "<svg",
        '<svg xmlns="http://www.w3.org/2000/svg"',
      );
    }

    const img = new Image();
    const svgBlob = new Blob([svgContent], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const loaded = await new Promise<boolean>((resolve) => {
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });

    if (loaded) {
      if (options.contain) {
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;
        let drawW = canvas.width;
        let drawH = canvas.height;
        let drawX = 0;
        let drawY = 0;

        if (imgAspect > canvasAspect) {
          drawH = canvas.width / imgAspect;
          drawY = (canvas.height - drawH) / 2;
        } else {
          drawW = canvas.height * imgAspect;
          drawX = (canvas.width - drawW) / 2;
        }
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
      } else {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }
    }
    URL.revokeObjectURL(url);
  } else {
    ctx.fillStyle = "darkblue";
    ctx.font = `italic ${canvas.height * 0.7}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(filled.value, canvas.width / 2, canvas.height / 2);
  }

  return await pdfDoc.embedPng(canvas.toDataURL("image/png"));
}
